import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../../lib/supabaseClient'

type Service = {
  id: string
  name: string
  description: string | null
  base_price: number
  is_active: boolean
  image_url: string | null
}

type ServiceOption = {
  id: string
  service_id: string
  label: string
  code: string
  description: string | null
  price_delta: number
  is_required: boolean
  is_active: boolean
  image_urls: string[]
}

const BUCKET = 'service-images'

const uploadImage = async (file: File, folder: string): Promise<string | null> => {
  const ext = file.name.split('.').pop()
  const path = `${folder}/${crypto.randomUUID()}.${ext}`
  const { error } = await supabase.storage.from(BUCKET).upload(path, file)
  if (error) {
    console.error('Upload error:', error)
    return null
  }
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path)
  return data.publicUrl
}

const deleteImage = async (url: string) => {
  const marker = `/storage/v1/object/public/${BUCKET}/`
  const idx = url.indexOf(marker)
  if (idx === -1) return
  const path = url.substring(idx + marker.length)
  await supabase.storage.from(BUCKET).remove([path])
}

const AdminServicesPage: React.FC = () => {
  const [services, setServices] = useState<Service[]>([])
  const [options, setOptions] = useState<ServiceOption[]>([])
  const [loading, setLoading] = useState(true)

  // Services utilisés (présents dans orders.service_id)
  const [usedServiceIds, setUsedServiceIds] = useState<Set<string>>(new Set())
  // Options utilisées (présentes dans order_options.option_id)
  const [usedOptionIds, setUsedOptionIds] = useState<Set<string>>(new Set())

  // Formulaire création service
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [price, setPrice] = useState<number>(30)
  const [serviceImageFile, setServiceImageFile] = useState<File | null>(null)
  const serviceImageRef = useRef<HTMLInputElement>(null)

  // Formulaire création option
  const [selectedServiceId, setSelectedServiceId] = useState<string>('')
  const [optLabel, setOptLabel] = useState('')
  const [optCode, setOptCode] = useState('')
  const [optDescription, setOptDescription] = useState('')
  const [optPriceDelta, setOptPriceDelta] = useState<number>(0)
  const [optImageFiles, setOptImageFiles] = useState<File[]>([])
  const optImageRef = useRef<HTMLInputElement>(null)

  // Édition service
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null)
  const [editServiceName, setEditServiceName] = useState('')
  const [editServiceDescription, setEditServiceDescription] = useState('')
  const [editServicePrice, setEditServicePrice] = useState<number>(0)
  const [editServiceImageFile, setEditServiceImageFile] = useState<File | null>(null)
  const [editServiceImageUrl, setEditServiceImageUrl] = useState<string | null>(null)
  const editServiceImageRef = useRef<HTMLInputElement>(null)

  // Édition option
  const [editingOptionId, setEditingOptionId] = useState<string | null>(null)
  const [editOptLabel, setEditOptLabel] = useState('')
  const [editOptCode, setEditOptCode] = useState('')
  const [editOptDescription, setEditOptDescription] = useState('')
  const [editOptPriceDelta, setEditOptPriceDelta] = useState<number>(0)
  const [editOptImageFiles, setEditOptImageFiles] = useState<File[]>([])
  const [editOptImageUrls, setEditOptImageUrls] = useState<string[]>([])
  const editOptImageRef = useRef<HTMLInputElement>(null)

  const load = async () => {
    setLoading(true)

    const [{ data: s, error: es }, { data: o, error: eo }] = await Promise.all([
      supabase.from('services').select('*').order('name'),
      supabase.from('service_options').select('*')
    ])

    if (es) console.error(es)
    if (eo) console.error(eo)

    setServices(s ?? [])
    setOptions((o as ServiceOption[]) ?? [])

    // Charger les ids utilisés dans des commandes
    const [usedServicesRes, usedOptionsRes] = await Promise.all([
      supabase.from('orders').select('service_id'),
      supabase.from('order_options').select('option_id')
    ])

    const serviceIds = new Set<string>()
    const optionIds = new Set<string>()

    if (!usedServicesRes.error) {
      for (const row of usedServicesRes.data ?? []) {
        if ((row as any).service_id) serviceIds.add((row as any).service_id as string)
      }
    }

    if (!usedOptionsRes.error) {
      for (const row of usedOptionsRes.data ?? []) {
        if ((row as any).option_id) optionIds.add((row as any).option_id as string)
      }
    }

    setUsedServiceIds(serviceIds)
    setUsedOptionIds(optionIds)

    setLoading(false)
  }

  useEffect(() => {
    void load()
  }, [])

  // --- Création ---

  const handleCreateService = async (e: React.FormEvent) => {
    e.preventDefault()
    let imageUrl: string | null = null
    if (serviceImageFile) {
      imageUrl = await uploadImage(serviceImageFile, 'services')
    }
    const { error } = await supabase.from('services').insert({
      name,
      description: description || null,
      base_price: price,
      image_url: imageUrl
    })
    if (error) {
      console.error(error)
      alert('Erreur lors de la création du service.')
      return
    }
    setName('')
    setDescription('')
    setPrice(30)
    setServiceImageFile(null)
    if (serviceImageRef.current) serviceImageRef.current.value = ''
    void load()
  }

  const handleCreateOption = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedServiceId) {
      alert('Choisir un service pour l’option.')
      return
    }
    const imageUrls: string[] = []
    for (const file of optImageFiles) {
      const url = await uploadImage(file, 'options')
      if (url) imageUrls.push(url)
    }
    const { error } = await supabase.from('service_options').insert({
      service_id: selectedServiceId,
      label: optLabel,
      code: optCode,
      description: optDescription || null,
      price_delta: optPriceDelta,
      is_active: true,
      image_urls: imageUrls
    })
    if (error) {
      console.error(error)
      alert("Erreur lors de la création de l’option.")
      return
    }
    setOptLabel('')
    setOptCode('')
    setOptDescription('')
    setOptPriceDelta(0)
    setOptImageFiles([])
    if (optImageRef.current) optImageRef.current.value = ''
    void load()
  }

  // --- Activation / désactivation ---

  const handleToggleServiceActive = async (service: Service) => {
    const { error } = await supabase
      .from('services')
      .update({ is_active: !service.is_active })
      .eq('id', service.id)

    if (error) {
      console.error(error)
      alert('Erreur lors de la mise à jour du service.')
      return
    }
    void load()
  }

  // ✅ NEW: toggle options (autorisé même si utilisée)
  const handleToggleOptionActive = async (opt: ServiceOption) => {
    const { error } = await supabase
      .from('service_options')
      .update({ is_active: !opt.is_active })
      .eq('id', opt.id)

    if (error) {
      console.error(error)
      alert("Erreur lors de la mise à jour de l’option.")
      return
    }
    void load()
  }

  // --- Suppression ---

  const handleDeleteService = async (service: Service) => {
    const used = usedServiceIds.has(service.id)
    if (used) {
      alert('Ce service est déjà utilisé dans au moins une commande, il ne peut pas être supprimé.')
      return
    }

    const ok = window.confirm(
      `Supprimer le service "${service.name}" ?\n\nAttention: cette suppression est définitive.`
    )
    if (!ok) return

    const { error } = await supabase.from('services').delete().eq('id', service.id)

    if (error) {
      console.error(error)
      alert('Impossible de supprimer ce service.')
      return
    }

    void load()
  }

  const handleDeleteOption = async (opt: ServiceOption) => {
    const used = usedOptionIds.has(opt.id)
    if (used) {
      alert('Cette option est déjà utilisée dans au moins une commande, elle ne peut pas être supprimée.\n\nDésactive-la plutôt.')
      return
    }

    const ok = window.confirm(
      `Supprimer l’option "${opt.label}" (${opt.code}) ?\n\nLes commandes existantes garderont leur snapshot.`
    )
    if (!ok) return

    const { error } = await supabase.from('service_options').delete().eq('id', opt.id)

    if (error) {
      console.error(error)
      alert('Impossible de supprimer cette option.')
      return
    }

    setOptions(prev => prev.filter(o => o.id !== opt.id))
  }

  // --- Édition service ---

  const startEditService = (service: Service) => {
    setEditingServiceId(service.id)
    setEditServiceName(service.name)
    setEditServiceDescription(service.description ?? '')
    setEditServicePrice(service.base_price)
    setEditServiceImageUrl(service.image_url)
    setEditServiceImageFile(null)
  }

  const cancelEditService = () => {
    setEditingServiceId(null)
  }

  const saveEditService = async (service: Service) => {
    const used = usedServiceIds.has(service.id)
    if (used) {
      alert('Ce service est déjà utilisé dans au moins une commande, il ne peut plus être modifié.')
      return
    }

    let imageUrl = editServiceImageUrl
    if (editServiceImageFile) {
      if (service.image_url) await deleteImage(service.image_url)
      imageUrl = await uploadImage(editServiceImageFile, 'services')
    }

    const { error } = await supabase
      .from('services')
      .update({
        name: editServiceName,
        description: editServiceDescription || null,
        base_price: editServicePrice,
        image_url: imageUrl
      })
      .eq('id', service.id)

    if (error) {
      console.error(error)
      alert('Erreur lors de la mise à jour du service.')
      return
    }

    setEditingServiceId(null)
    void load()
  }

  // --- Édition option ---

  const startEditOption = (opt: ServiceOption) => {
    setEditingOptionId(opt.id)
    setEditOptLabel(opt.label)
    setEditOptCode(opt.code)
    setEditOptDescription(opt.description ?? '')
    setEditOptPriceDelta(opt.price_delta)
    setEditOptImageUrls([...(opt.image_urls ?? [])])
    setEditOptImageFiles([])
  }

  const cancelEditOption = () => {
    setEditingOptionId(null)
  }

  const handleRemoveServiceImage = async (service: Service) => {
    if (service.image_url) await deleteImage(service.image_url)
    await supabase.from('services').update({ image_url: null }).eq('id', service.id)
    void load()
  }

  const handleUploadServiceImage = async (service: Service, file: File) => {
    if (service.image_url) await deleteImage(service.image_url)
    const url = await uploadImage(file, 'services')
    if (!url) {
      alert("Erreur lors de l'upload de l'image.")
      return
    }
    await supabase.from('services').update({ image_url: url }).eq('id', service.id)
    void load()
  }

  const handleRemoveOptionImage = async (opt: ServiceOption, urlToRemove: string) => {
    await deleteImage(urlToRemove)
    const newUrls = (opt.image_urls ?? []).filter(u => u !== urlToRemove)
    await supabase.from('service_options').update({ image_urls: newUrls }).eq('id', opt.id)
    void load()
  }


  const saveEditOption = async (opt: ServiceOption) => {
    const used = usedOptionIds.has(opt.id)

    // Upload new images
    const newUrls: string[] = []
    for (const file of editOptImageFiles) {
      const url = await uploadImage(file, 'options')
      if (url) newUrls.push(url)
    }
    const finalUrls = [...editOptImageUrls, ...newUrls]

    // Si l'option est utilisée dans une commande, on ne modifie que le nom, le code et la description
    const updatePayload = used
      ? {
          label: editOptLabel,
          code: editOptCode,
          description: editOptDescription || null,
        }
      : {
          label: editOptLabel,
          code: editOptCode,
          description: editOptDescription || null,
          price_delta: editOptPriceDelta,
          image_urls: finalUrls,
        }

    const { error } = await supabase
      .from('service_options')
      .update(updatePayload)
      .eq('id', opt.id)

    if (error) {
      console.error(error)
      alert("Erreur lors de la mise à jour de l’option.")
      return
    }

    setEditingOptionId(null)
    void load()
  }

  const optionsByService = (serviceId: string) => options.filter(o => o.service_id === serviceId)

  return (
    <div>
      <h2 className="card-title">Services & options</h2>

      {loading && <p>Chargement…</p>}

      {!loading && (
        <>
          {/* Liste des services */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '.95rem', marginBottom: '.5rem' }}>Services existants</h3>

            {services.map(service => {
              const used = usedServiceIds.has(service.id)
              const isEditing = editingServiceId === service.id

              return (
                <div
                  key={service.id}
                  data-testid={`service-${service.id}`}
                  style={{
                    borderTop: '1px solid rgba(148,163,184,.4)',
                    padding: '.75rem 0'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: '1rem'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      {isEditing ? (
                        <>
                          <div style={{ marginBottom: '.4rem' }}>
                            <label className="label">Nom</label>
                            <input
                              data-testid="service-edit-name"
                              className="input"
                              value={editServiceName}
                              onChange={e => setEditServiceName(e.target.value)}
                            />
                          </div>
                          <div style={{ marginBottom: '.4rem' }}>
                            <label className="label">Description</label>
                            <input
                              data-testid="service-edit-description"
                              className="input"
                              value={editServiceDescription}
                              onChange={e => setEditServiceDescription(e.target.value)}
                            />
                          </div>
                          <div style={{ marginBottom: '.4rem' }}>
                            <label className="label">Prix de base (CHF)</label>
                            <input
                              data-testid="service-edit-price"
                              className="input"
                              type="number"
                              step="0.1"
                              value={editServicePrice}
                              onChange={e => setEditServicePrice(Number(e.target.value))}
                            />
                          </div>
                          <div style={{ marginBottom: '.4rem' }}>
                            <label className="label">Photo du service</label>
                            {editServiceImageUrl && (
                              <div style={{ marginBottom: '.25rem' }}>
                                <img src={editServiceImageUrl} alt="Service" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }} />
                                <button type="button" className="btn btn-ghost" style={{ fontSize: '.7rem' }}
                                  onClick={() => setEditServiceImageUrl(null)}>Retirer</button>
                              </div>
                            )}
                            <input
                              data-testid="service-edit-image"
                              ref={editServiceImageRef}
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              onChange={e => setEditServiceImageFile(e.target.files?.[0] ?? null)}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div data-testid={`service-name-${service.id}`} style={{ fontWeight: 600, opacity: service.is_active ? 1 : 0.5 }}>
                            {service.name}
                          </div>
                          <div style={{ fontSize: '.8rem', color: '#9ca3af' }}>
                            {service.description}
                          </div>
                          {service.image_url && (
                            <div style={{ marginTop: '.25rem' }}>
                              <img data-testid={`service-image-${service.id}`} src={service.image_url} alt={service.name} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }} />
                            </div>
                          )}
                          <div style={{ fontSize: '.85rem', marginTop: '.25rem' }}>
                            Prix de base : <strong>{service.base_price.toFixed(2)} CHF</strong>
                          </div>
                          {!service.is_active && (
                            <div style={{ fontSize: '.75rem', color: '#f87171', marginTop: '.25rem' }}>
                              Service désactivé
                            </div>
                          )}
                          {used && (
                            <div style={{ fontSize: '.75rem', color: '#fbbf24', marginTop: '.25rem' }}>
                              Ce service est déjà utilisé dans au moins une commande, il n’est plus modifiable.
                            </div>
                          )}                          {used && (
                            <div style={{ marginTop: '.35rem' }}>
                              <label className="label" style={{ fontSize: '.75rem' }}>Photo du service</label>
                              <input
                                data-testid={`service-image-upload-${service.id}`}
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={e => {
                                  const file = e.target.files?.[0]
                                  if (file) void handleUploadServiceImage(service, file)
                                  e.target.value = ''
                                }}
                              />
                              {service.image_url && (
                                <button
                                  type="button"
                                  data-testid={`service-image-remove-${service.id}`}
                                  className="btn btn-ghost"
                                  style={{ fontSize: '.7rem', marginTop: '.15rem' }}
                                  onClick={() => handleRemoveServiceImage(service)}
                                >
                                  Supprimer l'image
                                </button>
                              )}
                            </div>
                          )}                        </>
                      )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '.25rem' }}>
                            <button type="button" data-testid={`service-toggle-${service.id}`} className="btn" onClick={() => handleToggleServiceActive(service)}>
                                {service.is_active ? 'Désactiver' : 'Activer'}
                              </button>

                      {isEditing ? (
                        <>
                          <button type="button" data-testid={`service-save-${service.id}`} className="btn btn-primary" onClick={() => saveEditService(service)}>
                            Enregistrer
                          </button>
                          <button type="button" data-testid={`service-cancel-${service.id}`} className="btn btn-ghost" onClick={cancelEditService}>
                            Annuler
                          </button>
                        </>
                      ) : (
                        <>
                          {!used && (
                                  <button type="button" data-testid={`service-edit-${service.id}`} className="btn btn-ghost" onClick={() => startEditService(service)}>
                              Modifier
                            </button>
                          )}
                          <button type="button" data-testid={`service-delete-${service.id}`} className="btn btn-ghost" onClick={() => handleDeleteService(service)}>
                            Supprimer
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Options du service */}
                  <div style={{ marginTop: '.5rem', paddingLeft: '.5rem' }}>
                    <div style={{ fontSize: '.8rem', color: '#9ca3af' }}>Options :</div>

                    {optionsByService(service.id).length === 0 && (
                      <div style={{ fontSize: '.8rem', color: '#6b7280' }}>Aucune option</div>
                    )}

                    {optionsByService(service.id).map(opt => {
                      const optUsed = usedOptionIds.has(opt.id)
                      const optEditing = editingOptionId === opt.id

                      return (
                        <div
                          key={opt.id}
                          data-testid={`service-option-${opt.id}`}
                          style={{
                            fontSize: '.8rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '.5rem',
                            marginTop: '.25rem'
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            {optEditing ? (
                              <>
                                <div style={{ marginBottom: '.25rem' }}>
                                  <label className="label">Libellé</label>
                                  <input
                                    data-testid="service-option-edit-label"
                                    className="input"
                                    value={editOptLabel}
                                    onChange={e => setEditOptLabel(e.target.value)}
                                  />
                                </div>
                                <div style={{ marginBottom: '.25rem' }}>
                                  <label className="label">Code</label>
                                  <input
                                    data-testid="service-option-edit-code"
                                    className="input"
                                    value={editOptCode}
                                    onChange={e => setEditOptCode(e.target.value)}
                                  />
                                </div>
                                <div style={{ marginBottom: '.25rem' }}>
                                  <label className="label">Surcoût (CHF)</label>
                                  <input
                                    data-testid="service-option-edit-pricedelta"
                                    className="input"
                                    type="number"
                                    step="0.1"
                                    value={editOptPriceDelta}
                                    onChange={e => setEditOptPriceDelta(Number(e.target.value))}
                                    disabled={optUsed}
                                    style={optUsed ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                                  />
                                </div>
                                <div style={{ marginBottom: '.25rem' }}>
                                  <label className="label">Description</label>
                                  <textarea
                                    data-testid="service-option-edit-description"
                                    className="input"
                                    value={editOptDescription}
                                    onChange={e => setEditOptDescription(e.target.value)}
                                    rows={2}
                                  />
                                </div>
                                {!optUsed && (
                                <div>
                                  <label className="label">Photos</label>
                                  {editOptImageUrls.length > 0 && (
                                    <div style={{ display: 'flex', gap: '.25rem', flexWrap: 'wrap', marginBottom: '.25rem' }}>
                                      {editOptImageUrls.map((url, i) => (
                                        <div key={i} style={{ position: 'relative' }}>
                                          <img src={url} alt={`Option ${i + 1}`} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                                          <button type="button" style={{ position: 'absolute', top: -4, right: -4, fontSize: '.6rem', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: 16, height: 16, cursor: 'pointer' }}
                                            onClick={() => setEditOptImageUrls(prev => prev.filter((_, j) => j !== i))}>×</button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  <input
                                    data-testid="service-option-edit-images"
                                    ref={editOptImageRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    multiple
                                    onChange={e => setEditOptImageFiles(Array.from(e.target.files ?? []))}
                                  />
                                </div>
                                )}
                                {optUsed && (
                                  <div style={{ fontSize: '.7rem', color: '#fbbf24', marginTop: '.25rem' }}>
                                    Option utilisée dans des commandes : le surcoût ne peut plus être modifié.
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                <div data-testid={`option-info-${opt.id}`} style={{ opacity: opt.is_active ? 1 : 0.5 }}>
                                  • {opt.label} ({opt.code}) : {opt.price_delta >= 0 ? '+' : ''}
                                  {opt.price_delta.toFixed(2)} CHF
                                </div>

                                {opt.description && (
                                  <div style={{ fontSize: '.7rem', color: '#9ca3af', marginTop: '.15rem' }}>
                                    {opt.description}
                                  </div>
                                )}

                                {opt.image_urls && opt.image_urls.length > 0 && (
                                  <div style={{ display: 'flex', gap: '.25rem', flexWrap: 'wrap', marginTop: '.25rem' }}>
                                    {opt.image_urls.map((url, i) => (
                                      <img key={i} data-testid={`option-image-${opt.id}-${i}`} src={url} alt={`${opt.label} ${i + 1}`} style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }} />
                                    ))}
                                  </div>
                                )}

                                {!opt.is_active && (
                                  <div style={{ fontSize: '.7rem', color: '#f87171', marginTop: '.15rem' }}>
                                    Option désactivée
                                  </div>
                                )}

                                {optUsed && (
                                  <div style={{ fontSize: '.7rem', color: '#fbbf24', marginTop: '.15rem' }}>
                                    Option déjà utilisée dans des commandes
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '.25rem' }}>
                            {/* ✅ toggle toujours autorisé */}
                            {!optEditing && (
                              <button
                                  type="button"
                                  data-testid={`service-option-toggle-${opt.id}`}
                                  className="btn"
                                  onClick={() => handleToggleOptionActive(opt)}
                                  style={{ fontSize: '.75rem', paddingInline: '.5rem' }}
                                >
                                  {opt.is_active ? 'Désactiver' : 'Activer'}
                                </button>
                            )}

                            {optEditing ? (
                              <>
                                <button
                                  type="button"
                                  data-testid={`service-option-save-${opt.id}`}
                                  className="btn btn-primary"
                                  onClick={() => saveEditOption(opt)}
                                  style={{ fontSize: '.75rem', paddingInline: '.5rem' }}
                                >
                                  Enregistrer
                                </button>
                                <button
                                  type="button"
                                  data-testid={`service-option-cancel-${opt.id}`}
                                  className="btn btn-ghost"
                                  onClick={cancelEditOption}
                                  style={{ fontSize: '.75rem', paddingInline: '.5rem' }}
                                >
                                  Annuler
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  data-testid={`service-option-edit-${opt.id}`}
                                  className="btn btn-ghost"
                                  onClick={() => startEditOption(opt)}
                                  style={{ fontSize: '.75rem', paddingInline: '.5rem' }}
                                >
                                  Modifier
                                </button>

                                {/* Suppression seulement si non utilisée */}
                                {!optUsed && (
                                  <button
                                    type="button"
                                    data-testid={`service-option-delete-${opt.id}`}
                                    className="btn btn-ghost"
                                    onClick={() => handleDeleteOption(opt)}
                                    style={{ fontSize: '.75rem', paddingInline: '.5rem' }}
                                  >
                                    Supprimer
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Création d’un service */}
          <form onSubmit={handleCreateService} style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '.95rem', marginBottom: '.5rem' }}>Nouveau service</h3>
            <div style={{ marginBottom: '.5rem' }}>
              <label className="label">Nom</label>
              <input data-testid="service-create-name" className="input" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div style={{ marginBottom: '.5rem' }}>
              <label className="label">Description</label>
              <input
                data-testid="service-create-description"
                className="input"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
            <div style={{ marginBottom: '.75rem' }}>
              <label className="label">Prix de base (CHF)</label>
              <input
                data-testid="service-create-price"
                className="input"
                type="number"
                step="0.1"
                value={price}
                onChange={e => setPrice(Number(e.target.value))}
              />
            </div>
            <div style={{ marginBottom: '.75rem' }}>
              <label className="label">Photo du service</label>
              <input
                data-testid="service-create-image"
                ref={serviceImageRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={e => setServiceImageFile(e.target.files?.[0] ?? null)}
              />
            </div>
            <button className="btn btn-primary" type="submit">
              Créer le service
            </button>
          </form>

          {/* Création d’une option */}
          <form onSubmit={handleCreateOption}>
            <h3 style={{ fontSize: '.95rem', marginBottom: '.5rem' }}>Nouvelle option de service</h3>

            <div style={{ marginBottom: '.5rem' }}>
              <label className="label">Service</label>
              <select
                data-testid="service-option-create-service"
                className="select"
                value={selectedServiceId}
                onChange={e => setSelectedServiceId(e.target.value)}
              >
                <option value="">Sélectionner…</option>
                {services.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.description})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '.5rem' }}>
              <label className="label">Libellé</label>
              <input data-testid="service-option-create-label" className="input" value={optLabel} onChange={e => setOptLabel(e.target.value)} />
            </div>

            <div style={{ marginBottom: '.5rem' }}>
              <label className="label">Code (clé interne)</label>
              <input data-testid="service-option-create-code" className="input" value={optCode} onChange={e => setOptCode(e.target.value)} />
            </div>

            <div style={{ marginBottom: '.5rem' }}>
              <label className="label">Surcoût (CHF, négatif possible)</label>
              <input
                data-testid="service-option-create-pricedelta"
                className="input"
                type="number"
                step="0.1"
                value={optPriceDelta}
                onChange={e => setOptPriceDelta(Number(e.target.value))}
              />
            </div>

            <div style={{ marginBottom: '.5rem' }}>
              <label className="label">Description de l’option</label>
              <textarea
                data-testid="service-option-create-description"
                className="input"
                value={optDescription}
                onChange={e => setOptDescription(e.target.value)}
                rows={2}
              />
            </div>

            <div style={{ marginBottom: '.75rem' }}>
              <label className="label">Photos de l’option</label>
              <input
                data-testid="service-option-create-images"
                ref={optImageRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                onChange={e => setOptImageFiles(Array.from(e.target.files ?? []))}
              />
            </div>

            <button data-testid="service-option-create-submit" className="btn btn-primary" type="submit">
              Ajouter l’option
            </button>
          </form>
        </>
      )}
    </div>
  )
}

export default AdminServicesPage
